'use client';

import { type PropsWithChildren, useMemo } from 'react';
import { noop } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import { createContext, useContextSelector } from 'use-context-selector';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { useDeleteWorkspace } from '../../operations';
import type {
  UseCreateProjectModalReturn,
  UseCreateWorkspaceModalReturn,
  UseDeleteProjectConfirmationModalReturn,
  UseDuplicateProjectModalReturn,
  UseProjectSettingsModalReturn,
  UseWorkspaceNotificationsWarningModalReturn,
} from '../hooks';
import {
  useCreateProjectModal,
  useCreateWorkspaceModal,
  useDeleteProjectConfirmationModal,
  useDuplicateProjectModal,
  useProjectSettingsModal,
  useWorkspaceNotificationsWarningModal,
} from '../hooks';

type SettingsContextValue = {
  showCreateWorkspaceModal: UseCreateWorkspaceModalReturn['showCreateWorkspaceModal'];
  hideCreateWorkspaceModal: UseCreateWorkspaceModalReturn['hideCreateWorkspaceModal'];
  showCreateProjectModal: UseCreateProjectModalReturn['showCreateProjectModal'];
  hideCreateProjectModal: UseCreateProjectModalReturn['hideCreateProjectModal'];
  showProjectSettingsModal: UseProjectSettingsModalReturn['showProjectSettingsModal'];
  hideProjectSettingsModal: UseProjectSettingsModalReturn['hideProjectSettingsModal'];
  showDeleteProjectConfirmationModal: UseDeleteProjectConfirmationModalReturn['showDeleteProjectConfirmationModal'];
  showWorkspaceNotificationsWarningModal: UseWorkspaceNotificationsWarningModalReturn['showWorkspaceNotificationsWarningModal'];
  hideWorkspaceNotificationsWarningModal: UseWorkspaceNotificationsWarningModalReturn['hideWorkspaceNotificationsWarningModal'];
  showDeleteConfirmation: ReturnType<
    typeof useDeleteWorkspace
  >['showDeleteConfirmation'];
  showDuplicateProjectModal: UseDuplicateProjectModalReturn['showDuplicateProjectModal'];
  hideDuplicateProjectModal: UseDuplicateProjectModalReturn['hideDuplicateProjectModal'];
};

const SettingsContext = createContext<SettingsContextValue>({
  showCreateWorkspaceModal: noop,
  hideCreateWorkspaceModal: noop,
  showCreateProjectModal: noop,
  hideCreateProjectModal: noop,
  showProjectSettingsModal: noop,
  hideProjectSettingsModal: noop,
  showDeleteProjectConfirmationModal: noop,
  showWorkspaceNotificationsWarningModal: noop,
  hideWorkspaceNotificationsWarningModal: noop,
  showDeleteConfirmation: noop,
  showDuplicateProjectModal: noop,
  hideDuplicateProjectModal: noop,
});

export const SettingsContextProvider = ({ children }: PropsWithChildren) => {
  const accountId = useActiveAccountId();
  const router = useRouter();
  const pathname = usePathname();

  const {
    createWorkspaceModal,
    showCreateWorkspaceModal,
    hideCreateWorkspaceModal,
  } = useCreateWorkspaceModal();

  const { createProjectModal, showCreateProjectModal, hideCreateProjectModal } =
    useCreateProjectModal();

  const {
    projectSettingsModal,
    showProjectSettingsModal,
    hideProjectSettingsModal,
  } = useProjectSettingsModal();

  const {
    workspaceNotificationsWarningModal,
    showWorkspaceNotificationsWarningModal,
    hideWorkspaceNotificationsWarningModal,
  } = useWorkspaceNotificationsWarningModal();

  const { deleteProjectModal, showDeleteProjectConfirmationModal } =
    useDeleteProjectConfirmationModal();

  const { deleteDialog, showDeleteConfirmation } = useDeleteWorkspace({
    accountId,
    onSuccess(workspace) {
      if (workspace.id === pathname.split('/')[2]) {
        router.push(
          getLink({ page: 'workspaces', explicitWorkspacesPath: true })
        );
      }
    },
  });

  const {
    duplicateProjectModal,
    showDuplicateProjectModal,
    hideDuplicateProjectModal,
  } = useDuplicateProjectModal();

  const value = useMemo(() => {
    return {
      showCreateWorkspaceModal,
      hideCreateWorkspaceModal,
      showCreateProjectModal,
      hideCreateProjectModal,
      showProjectSettingsModal,
      hideProjectSettingsModal,
      showDeleteConfirmation,
      showWorkspaceNotificationsWarningModal,
      hideWorkspaceNotificationsWarningModal,
      showDeleteProjectConfirmationModal,
      showDuplicateProjectModal,
      hideDuplicateProjectModal,
    };
  }, [
    showCreateWorkspaceModal,
    hideCreateWorkspaceModal,
    showCreateProjectModal,
    hideCreateProjectModal,
    showProjectSettingsModal,
    hideProjectSettingsModal,
    showDeleteConfirmation,
    showWorkspaceNotificationsWarningModal,
    hideWorkspaceNotificationsWarningModal,
    showDeleteProjectConfirmationModal,
    showDuplicateProjectModal,
    hideDuplicateProjectModal,
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}

      {createWorkspaceModal}
      {createProjectModal}
      {projectSettingsModal}
      {workspaceNotificationsWarningModal}
      {deleteProjectModal}
      {workspaceNotificationsWarningModal}
      {deleteDialog}
      {duplicateProjectModal}
    </SettingsContext.Provider>
  );
};

export const useShowCreateWorkspaceModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.showCreateWorkspaceModal
  );

export const useHideCreateWorkspaceModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.hideCreateWorkspaceModal
  );

export const useShowCreateProjectModal = () =>
  useContextSelector(SettingsContext, (value) => {
    return value.showCreateProjectModal;
  });

export const useHideCreateProjectModal = () =>
  useContextSelector(SettingsContext, (value) => value.hideCreateProjectModal);

export const useShowProjectSettingsModal = () =>
  useContextSelector(SettingsContext, (value) => {
    return value.showProjectSettingsModal;
  });

export const useHideProjectSettingsModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.hideProjectSettingsModal
  );

export const useShowWorkspaceNotificationsWarningModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.showWorkspaceNotificationsWarningModal
  );

export const useHideWorkspaceNotificationsWarningModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.hideWorkspaceNotificationsWarningModal
  );

export const useDeleteWorkspaceWithWarning = () =>
  useContextSelector(
    SettingsContext,
    ({ showDeleteConfirmation }) => showDeleteConfirmation
  );

export const useShowDeleteProjectConfirmationModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.showDeleteProjectConfirmationModal
  );

export const useShowDuplicateProjectModal = () =>
  useContextSelector(
    SettingsContext,
    (value) => value.showDuplicateProjectModal
  );
